import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, Clock3, PauseCircle, PlayCircle, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { cn } from '@/lib/utils';
import type { EqmsUpdateRequest } from '../../../types/eqms.types';
import { UPDATE_REQUEST_STATUS_STYLES } from './statusStyles';
import { formatRequestDate, useUpdateRequestActions } from './useUpdateRequestActions';

type NoteDecision = 'parked' | 'declined' | 'done';
const DECISION_COPY: Record<NoteDecision, { title: string; hint: string }> = {
  parked: { title: 'updateRequests.decision.parkTitle', hint: 'updateRequests.decision.parkHint' },
  declined: { title: 'updateRequests.decision.declineTitle', hint: 'updateRequests.decision.declineHint' },
  done: { title: 'updateRequests.decision.doneTitle', hint: 'updateRequests.decision.doneHint' },
};

interface UpdateRequestCardProps {
  request: EqmsUpdateRequest;
  /** Owners decide; requesters only follow the decision. */
  mode: 'owner' | 'requester';
  showDocument?: boolean;
}

export function UpdateRequestCard({ request, mode, showDocument = false }: UpdateRequestCardProps) {
  const { t, i18n } = useTranslation('qms');
  const { decide, addressNow } = useUpdateRequestActions();
  const [decision, setDecision] = useState<NoteDecision | null>(null);
  const [note, setNote] = useState('');
  const pending = decide.isPending || addressNow.isPending;
  const canDecide = mode === 'owner' && ['open', 'addressing', 'parked'].includes(request.status);
  const noteRequired = decision === 'declined';
  const decided = request.status !== 'open' && request.decidedAt;

  const openDecision = (next: NoteDecision) => { setNote(''); decide.reset(); setDecision(next); };
  const confirmDecision = () => {
    if (!decision) return;
    decide.mutate({ request, status: decision, note }, { onSuccess: () => setDecision(null) });
  };

  return (
    <article className="rounded-xl border border-slate-200 bg-white p-4" aria-label={request.reason.slice(0, 80)}>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className={cn('font-medium', UPDATE_REQUEST_STATUS_STYLES[request.status])}>
            {t(`updateRequests.status.${request.status}`)}
          </Badge>
          <span className="text-sm font-medium text-slate-900">
            {t('updateRequests.requestedBy', { name: request.requesterName, date: formatRequestDate(request.createdAt, i18n.language) })}
          </span>
        </div>
        {request.documentVersion && (
          <span className="font-mono text-xs text-slate-500">{t('updateRequests.versionRead', { version: request.documentVersion })}</span>
        )}
      </div>
      {showDocument && (
        <p className="mt-1 text-xs text-slate-500">
          <span className="font-mono">{request.document.controlCode}</span> · {request.document.name}
        </p>
      )}
      <p className="mt-3 whitespace-pre-wrap break-words text-sm text-slate-800">{request.reason}</p>

      {decided && (
        <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
          <p className="text-xs text-slate-500">
            {t('updateRequests.decidedBy', { name: request.decidedByName || '—', date: formatRequestDate(request.decidedAt, i18n.language) })}
            {request.resolvedVersion && <> · {t('updateRequests.resolvedIn', { version: request.resolvedVersion })}</>}
          </p>
          {request.decisionNote && <p className="mt-1 whitespace-pre-wrap break-words">{request.decisionNote}</p>}
        </div>
      )}

      {request.history.length > 1 && (
        <details className="mt-3 text-xs text-slate-500">
          <summary className="cursor-pointer select-none">{t('updateRequests.history', { count: request.history.length })}</summary>
          <ol className="mt-2 space-y-1 border-l border-slate-200 pl-3">
            {request.history.map((event, index) => (
              <li key={`${event.createdAt}-${index}`}>
                <span className="font-medium text-slate-700">{t(`updateRequests.status.${event.toStatus}`)}</span>
                {' · '}{event.actorName || '—'}{' · '}{formatRequestDate(event.createdAt, i18n.language)}
                {event.note && <span className="block text-slate-600">{event.note}</span>}
              </li>
            ))}
          </ol>
        </details>
      )}

      {canDecide && (
        <div className="mt-4 flex flex-wrap gap-2">
          {request.status !== 'addressing' && (
            <Button size="sm" className="bg-teal-700 hover:bg-teal-800" disabled={pending} onClick={() => addressNow.mutate(request)}>
              <PlayCircle className="mr-1.5 h-4 w-4" />{t('updateRequests.actions.address')}
            </Button>
          )}
          {request.status !== 'parked' && (
            <Button size="sm" variant="outline" disabled={pending} onClick={() => openDecision('parked')}>
              <PauseCircle className="mr-1.5 h-4 w-4" />{t('updateRequests.actions.park')}
            </Button>
          )}
          <Button size="sm" variant="outline" disabled={pending} onClick={() => openDecision('done')}>
            <CheckCircle2 className="mr-1.5 h-4 w-4" />{t('updateRequests.actions.done')}
          </Button>
          <Button size="sm" variant="ghost" className="text-slate-600" disabled={pending} onClick={() => openDecision('declined')}>
            <XCircle className="mr-1.5 h-4 w-4" />{t('updateRequests.actions.decline')}
          </Button>
        </div>
      )}
      {mode === 'owner' && request.status === 'addressing' && request.document.sourceKind === 'authored' && (
        <p className="mt-2 flex items-center gap-1.5 text-xs text-teal-800"><Clock3 className="h-3.5 w-3.5" />{t('updateRequests.autoClose')}</p>
      )}

      <Dialog open={decision !== null} onOpenChange={(open) => { if (!open && !decide.isPending) setDecision(null); }}>
        <DialogContent className="sm:max-w-md">
          {decision && (
            <>
              <DialogHeader>
                <DialogTitle>{t(DECISION_COPY[decision].title)}</DialogTitle>
                <DialogDescription>{t(DECISION_COPY[decision].hint)}</DialogDescription>
              </DialogHeader>
              <blockquote className="rounded-lg border-l-4 border-teal-600 bg-slate-50 px-3 py-2 text-sm text-slate-700">{request.reason}</blockquote>
              <div className="space-y-2">
                <Label htmlFor={`eqms-decision-note-${request.id}`}>
                  {t(noteRequired ? 'updateRequests.decision.noteRequired' : 'updateRequests.decision.note')}
                </Label>
                <Textarea id={`eqms-decision-note-${request.id}`} rows={3} maxLength={2000} value={note}
                  disabled={decide.isPending} onChange={(event) => setNote(event.target.value)} />
                <p className="text-xs text-muted-foreground">{t('updateRequests.decision.visible')}</p>
                {decide.isError && <p role="alert" className="text-sm text-red-700">{decide.error.message}</p>}
              </div>
              <DialogFooter>
                <Button variant="outline" disabled={decide.isPending} onClick={() => setDecision(null)}>{t('updateRequests.cancel')}</Button>
                <Button disabled={decide.isPending || (noteRequired && note.trim().length < 3)} onClick={confirmDecision}>
                  {t('updateRequests.decision.confirm')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </article>
  );
}
